import kioskApi from './kioskApi';
import settings from '../config/settings';

/**
 * Payment Service
 * 
 * This service handles all payment-related operations, including:
 * - Creating payment intents
 * - Confirming payments
 * - Processing payments
 * - Handling payment errors
 */

/**
 * Create a payment intent for a new order
 * @param {number} amount - Amount in cents
 * @param {string} orderId - Order ID (if available)
 * @param {Object} metadata - Additional metadata for the payment
 * @returns {Promise<Object>} Payment intent details
 */
export const createPaymentIntent = async (amount, orderId = null, metadata = {}) => {
  try {
    const paymentData = {
      amount,
      currency: settings.payment.currency || 'usd',
      metadata: {
        ...metadata,
        order_id: orderId,
        source: 'kiosk-app',
        kiosk_id: settings.kioskId || 'default-kiosk'
      }
    };

    return await kioskApi.payments.createIntent(amount, paymentData.currency, paymentData.metadata);
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error(error.message || 'Failed to create payment intent');
  }
};

/**
 * Confirm a payment intent
 * @param {string} paymentIntentId - The payment intent ID
 * @param {string} paymentMethodId - The payment method ID from Stripe Elements
 * @returns {Promise<Object>} The confirmed payment intent
 */
export const confirmPaymentIntent = async (paymentIntentId, paymentMethodId) => {
  try {
    if (!paymentIntentId || !paymentMethodId) {
      throw new Error('Payment intent ID and payment method ID are required');
    }

    return await kioskApi.payments.confirmIntent(paymentIntentId, paymentMethodId);
  } catch (error) {
    console.error('Error confirming payment intent:', error);
    throw new Error(error.message || 'Failed to confirm payment');
  }
};

/**
 * Process a payment for an order
 * @param {string} orderId - The order ID
 * @param {Object} paymentData - Payment data including amount, payment method, etc.
 * @returns {Promise<Object>} The payment result
 */
export const processPayment = async (orderId, paymentData) => {
  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    if (!paymentData || typeof paymentData !== 'object') {
      throw new Error('Payment data is required');
    }

    return await kioskApi.orders.processPayment(orderId, paymentData);
  } catch (error) {
    console.error('Error processing payment:', error);
    throw new Error(error.message || 'Failed to process payment');
  }
};

/**
 * Create an order and process payment in a single transaction
 * @param {Object} orderData - The order data
 * @param {Object} paymentData - The payment data
 * @returns {Promise<Object>} The created order and payment result
 */
export const createOrderAndProcessPayment = async (orderData, paymentData) => {
  try {
    // First, create the order
    const order = await kioskApi.orders.create(orderData);
    
    if (!order || !order.id) {
      throw new Error('Failed to create order');
    }

    // Then process the payment for the order
    const paymentResult = await processPayment(order.id, {
      ...paymentData,
      order_id: order.id
    });

    return {
      order,
      payment: paymentResult
    };
  } catch (error) {
    console.error('Error in createOrderAndProcessPayment:', error);
    throw error;
  }
};

/**
 * Format currency for display
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code (default: 'usd')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toLowerCase() === 'usd' ? 'USD' : currency,
    minimumFractionDigits: 2
  }).format(amount / 100); // Convert cents to dollars
};

export default {
  createPaymentIntent,
  confirmPaymentIntent,
  processPayment,
  createOrderAndProcessPayment,
  formatCurrency
};
